import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Lock, Unlock } from 'lucide-react';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PayloadCategoryChip } from '@/components/category-chip';
import { ReviewPopover } from '@/components/review-popover';
import { PAYLOAD_CATEGORIES } from '@/lib/constants';
import { useCurrentIdentity } from '@/lib/identity-context';
import { Pagination } from './scripts-tab';

type ConsentMode = components['schemas']['ConsentMode'];
type PayloadRecord = components['schemas']['PayloadRecord'];
type PayloadField = components['schemas']['PayloadField'];
type ReviewMap = Map<string, components['schemas']['FindingReview']>;

interface PayloadsTabProps {
  crawlId: string;
  consentMode: ConsentMode | 'all';
  gtmContainerId: string | 'all';
  reviews: ReviewMap;
  /**
   * URL-controlled destination-domain filter. Sourced from the route's
   * `?destinationDomain=` search param so deep-links (e.g. the
   * vendor-card "View payloads" button) survive a page reload and are
   * shareable.
   */
  destinationDomain: string;
  onDestinationDomainChange: (value: string) => void;
}

type ViewMode = 'records' | 'by-destination' | 'by-script';

export function PayloadsTab({
  crawlId,
  consentMode,
  gtmContainerId,
  reviews,
  destinationDomain,
  onDestinationDomainChange,
}: PayloadsTabProps) {
  const [view, setView] = useState<ViewMode>('records');
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Button size="sm" variant={view === 'records' ? 'default' : 'outline'} onClick={() => setView('records')}>
          All records
        </Button>
        <Button
          size="sm"
          variant={view === 'by-destination' ? 'default' : 'outline'}
          onClick={() => setView('by-destination')}
          data-testid="payload-view-by-destination"
        >
          By destination
        </Button>
        <Button
          size="sm"
          variant={view === 'by-script' ? 'default' : 'outline'}
          onClick={() => setView('by-script')}
        >
          By script
        </Button>
      </div>
      {view === 'records' && (
        <PayloadRecords
          crawlId={crawlId}
          consentMode={consentMode}
          gtmContainerId={gtmContainerId}
          reviews={reviews}
          destinationDomain={destinationDomain}
          onDestinationDomainChange={onDestinationDomainChange}
        />
      )}
      {view === 'by-destination' && <PayloadsByDestination crawlId={crawlId} />}
      {view === 'by-script' && <PayloadsByScript crawlId={crawlId} />}
    </div>
  );
}

function PayloadRecords({
  crawlId,
  consentMode,
  gtmContainerId,
  reviews,
  destinationDomain,
  onDestinationDomainChange,
}: PayloadsTabProps) {
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const filters = useMemo(
    () => ({
      page,
      pageSize,
      destinationDomain: destinationDomain || undefined,
      category: (category || undefined) as components['schemas']['PayloadCategory'] | undefined,
      consentMode: consentMode === 'all' ? undefined : consentMode,
      gtmContainerId: gtmContainerId === 'all' ? undefined : gtmContainerId,
    }),
    [page, destinationDomain, category, consentMode, gtmContainerId],
  );

  const query = useQuery({
    queryKey: queryKeys.crawls.payloads(crawlId, filters),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/payloads', {
        params: { path: { crawlId }, query: filters },
      });
      if (error) throw error;
      return data;
    },
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 rounded-md border bg-background p-2">
        <Input
          placeholder="Destination domain…"
          value={destinationDomain}
          onChange={(e) => onDestinationDomainChange(e.target.value)}
          className="h-8 w-60"
          data-testid="payload-destination-filter"
        />
        <Select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="h-8 w-48"
          data-testid="payload-category-filter"
        >
          <option value="">Any category</option>
          {PAYLOAD_CATEGORIES.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
      </div>
      <Table>
        <THead>
          <TR>
            <TH>Destination</TH>
            <TH>3P</TH>
            <TH>Method</TH>
            <TH>GTM</TH>
            <TH>Categories</TH>
            <TH>Fields</TH>
            <TH>Consent</TH>
            <TH>Review</TH>
          </TR>
        </THead>
        <TBody>
          {query.isLoading ? (
            <TR><TD colSpan={8} className="text-center text-xs text-muted-foreground">Loading…</TD></TR>
          ) : items.length === 0 ? (
            <TR><TD colSpan={8} className="text-center text-xs text-muted-foreground">No payloads matched.</TD></TR>
          ) : (
            items.map((p) => (
              <PayloadRow key={p.id} payload={p} crawlId={crawlId} reviews={reviews} />
            ))
          )}
        </TBody>
      </Table>
      <Pagination page={page} pageCount={pageCount} total={total} onChange={setPage} />
    </>
  );
}

function PayloadRow({
  payload,
  crawlId,
  reviews,
}: {
  payload: PayloadRecord;
  crawlId: string;
  reviews: ReviewMap;
}) {
  const { hasPermission } = useCurrentIdentity();
  const canReadRaw = hasPermission('payload:read-raw');
  const [expanded, setExpanded] = useState(false);
  const [raw, setRaw] = useState(canReadRaw);
  const review = reviews.get(`payload:${payload.id}`);

  // Auto-fetch raw values on row expand for permitted callers; the
  // API writes one audit row per fetch in `payload_raw_access_log`.
  // Gating on `expanded` keeps page loads from triggering an audit row
  // per visible payload. `gcTime: 0` ensures the un-redacted value is
  // discarded on unmount so a stale cache hit can't surface a raw
  // value without a fresh audited fetch.
  const rawQuery = useQuery({
    queryKey: ['payloads', payload.id, 'raw'],
    enabled: expanded && raw && canReadRaw,
    gcTime: 0,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/payloads/{payloadId}', {
        params: { path: { payloadId: payload.id }, query: { raw: true } },
      });
      if (error) throw error;
      return data;
    },
  });

  const queryFields =
    raw && rawQuery.data ? rawQuery.data.queryFields ?? payload.queryFields : payload.queryFields;
  const bodyFields =
    raw && rawQuery.data ? rawQuery.data.bodyFields ?? payload.bodyFields : payload.bodyFields;

  const bodyArray = Array.isArray(bodyFields) ? bodyFields : null;

  return (
    <>
      <TR data-testid={`payload-row-${payload.id}`}>
        <TD className="max-w-[260px]">
          <button className="truncate text-left font-mono text-xs hover:underline" onClick={() => setExpanded((x) => !x)}>
            {payload.destinationDomain}
          </button>
        </TD>
        <TD>{payload.isThirdParty ? <Badge variant="warning">3P</Badge> : <Badge variant="muted">1P</Badge>}</TD>
        <TD className="text-xs">{payload.method}</TD>
        <TD>{payload.gtmContainerId ? <Badge variant="info">GTM</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TD>
        <TD>
          <div className="flex flex-wrap gap-1">
            {(payload.payloadCategories ?? []).map((cat) => (
              <PayloadCategoryChip key={cat} category={cat} />
            ))}
          </div>
        </TD>
        <TD className="text-xs">
          {(payload.queryFields?.length ?? 0) + (bodyArray?.length ?? 0)}
        </TD>
        <TD className="text-xs">{payload.consentMode}</TD>
        <TD>
          <ReviewPopover
            crawlId={crawlId}
            entityType="payload"
            entityId={payload.id}
            currentStatus={review?.status ?? 'unreviewed'}
            currentNote={review?.note ?? null}
          />
        </TD>
      </TR>
      {expanded && (
        <TR>
          <TD colSpan={8} className="bg-muted/30">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px]">{payload.requestUrl}</div>
                {canReadRaw ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRaw((r) => !r)}
                    data-testid={`toggle-raw-${payload.id}`}
                  >
                    {raw ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                    {raw ? 'Hide raw' : 'Show raw'}
                  </Button>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Lock className="h-3 w-3" /> Raw redacted
                  </span>
                )}
              </div>
              <FieldList label="Query fields" fields={queryFields ?? []} />
              {bodyArray && <FieldList label="Body fields" fields={bodyArray} />}
              {bodyFields && !Array.isArray(bodyFields) && (
                <span className="text-xs text-muted-foreground">
                  Body overflowed to R2 (key: {bodyFields.overflowKey})
                </span>
              )}
            </div>
          </TD>
        </TR>
      )}
    </>
  );
}

function FieldList({ label, fields }: { label: string; fields: PayloadField[] }) {
  if (fields.length === 0) return <div className="text-xs text-muted-foreground">No {label.toLowerCase()}</div>;
  return (
    <div>
      <div className="mb-1 text-xs font-semibold text-muted-foreground">{label}</div>
      <ul className="grid gap-1 sm:grid-cols-2">
        {fields.map((f, idx) => (
          <li key={`${f.name}-${idx}`} className="flex items-center gap-2 rounded-md border px-2 py-1 text-[11px]">
            <PayloadCategoryChip category={f.classifiedCategory} />
            <span className="font-mono font-semibold">{f.name}</span>
            <span className="truncate text-muted-foreground">{f.redactedSample}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PayloadsByDestination({ crawlId }: { crawlId: string }) {
  const query = useQuery({
    queryKey: queryKeys.crawls.payloadsByDestination(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/payloads/by-destination', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });
  const items = query.data?.items ?? [];
  return (
    <Table>
      <THead>
        <TR>
          <TH>Destination</TH>
          <TH>Requests</TH>
          <TH>Categories</TH>
          <TH>Consent modes</TH>
        </TR>
      </THead>
      <TBody>
        {items.length === 0 ? (
          <TR><TD colSpan={4} className="text-center text-xs text-muted-foreground">No destinations.</TD></TR>
        ) : (
          items.map((d) => (
            <TR key={d.destinationDomain}>
              <TD className="font-mono text-xs">{d.destinationDomain}</TD>
              <TD>{d.requestCount}</TD>
              <TD>
                <div className="flex flex-wrap gap-1">
                  {(d.payloadCategories ?? []).map((c) => (
                    <PayloadCategoryChip key={c} category={c} />
                  ))}
                </div>
              </TD>
              <TD className="text-xs">{(d.consentModes ?? []).join(', ')}</TD>
            </TR>
          ))
        )}
      </TBody>
    </Table>
  );
}

function PayloadsByScript({ crawlId }: { crawlId: string }) {
  const query = useQuery({
    queryKey: queryKeys.crawls.payloadsByScript(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/payloads/by-script', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });
  const items = query.data?.items ?? [];
  return (
    <Table>
      <THead>
        <TR>
          <TH>Script</TH>
          <TH>Requests</TH>
          <TH>Destinations</TH>
          <TH>Categories</TH>
        </TR>
      </THead>
      <TBody>
        {items.length === 0 ? (
          <TR><TD colSpan={4} className="text-center text-xs text-muted-foreground">No scripts.</TD></TR>
        ) : (
          items.map((s, idx) => (
            <TR key={`${s.scriptId ?? 'noid'}-${idx}`}>
              <TD className="max-w-[320px] truncate font-mono text-xs">{s.scriptUrl}</TD>
              <TD>{s.requestCount}</TD>
              <TD className="text-xs">{s.destinations.length}</TD>
              <TD>
                <div className="flex flex-wrap gap-1">
                  {(s.payloadCategories ?? []).map((c) => (
                    <PayloadCategoryChip key={c} category={c} />
                  ))}
                </div>
              </TD>
            </TR>
          ))
        )}
      </TBody>
    </Table>
  );
}
